import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { Button } from "../components/Button";
import { Field } from "../components/Field";
import { Input } from "../components/Input";
import { VoiceRibbon } from "../components/VoiceRibbon";
import { ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      // RedirectIfAuthed routes to the original destination.
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? "Email or password is incorrect."
          : err instanceof Error
            ? err.message
            : "Could not sign in. Try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen bg-ink">
      {/* Form, left */}
      <div className="flex w-full flex-col justify-center px-6 sm:px-12 lg:w-[480px] lg:shrink-0 lg:border-r lg:border-line">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-10 flex items-center gap-2.5 text-bright">
            <svg viewBox="0 0 20 20" fill="none" className="h-6 w-6" aria-hidden="true">
              <path
                d="M2.5 10h1.2M6 10V6.8m0 3.2v3.2M10 10V3.5m0 6.5v6.5M14 10V7.5m0 2.5v2.5m3.5-2.5h-1.2"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
            <span className="font-display text-base font-medium tracking-display">Voice Agents</span>
          </div>

          <h1 className="mb-1 font-display text-xl tracking-display text-bright">Sign in</h1>
          <p className="mb-8 text-sm text-muted">Welcome back. Your agents are waiting.</p>

          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            <Field label="Email">
              <Input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </Field>
            <Field label="Password">
              <Input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </Field>

            {error && (
              <p className="rounded border border-danger/60 bg-danger/10 px-3 py-2 text-xs text-danger" role="alert">
                {error}
              </p>
            )}

            <Button type="submit" variant="primary" loading={submitting} className="mt-2 w-full">
              Sign in
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted">
            New here?{" "}
            <Link to="/register" className="text-body underline underline-offset-4 transition-colors hover:text-bright">
              Create an account
            </Link>
          </p>
        </div>
      </div>

      {/* Ribbon, right */}
      <div className="hidden flex-1 flex-col items-center justify-center gap-10 px-16 lg:flex">
        <VoiceRibbon state="idle" height={120} className="max-w-2xl" aria-label="Voice waveform" />
        <div className="max-w-md text-center">
          <p className="font-display text-lg tracking-display text-bright">
            Voice agents that answer the phone.
          </p>
          <p className="mt-2 text-sm text-muted">
            Build an agent, talk to it in your browser in thirty seconds, then put it on a real
            number.
          </p>
        </div>
      </div>
    </div>
  );
}
